import React from 'react';
import PropTypes from 'prop-types';
import { aiPriceState, aiPriceChipText, aiPriceLabel, AI_PRICE_STATES } from '../../utils/aiPrice';
import { gapDirection, formatPctSigned } from '../../utils/lotValue';
import { useDealershipSettings } from '../../contexts/settingsContext';
import './ui.css';

/** Small rounded status chip. */
export function Chip({ tone = 'neutral', children, className, ...rest }) {
    return (
        <span className={['v2-chip', `v2-chip--${tone}`, className].filter(Boolean).join(' ')} {...rest}>
            {children}
        </span>
    );
}

Chip.propTypes = {
    tone: PropTypes.oneOf(['ok', 'warn', 'crit', 'below', 'review', 'none', 'neutral', 'teal']),
    children: PropTypes.node,
    className: PropTypes.string,
};

// AI price state (utils/aiPrice.js) → chip tone. The thresholds are the
// dealership's gap scale (dealership settings, defaults GAP_THRESHOLDS in
// utils/lotValue.js); nothing here decides a state.
export const GAP_CHIP_TONES = Object.freeze({
    inline: 'ok',
    above: 'warn',
    wellAbove: 'crit',
    below: 'below',
    review: 'review',
    none: 'none',
});

const aggregateView = (gap, label, thresholds) => {
    const state = gap === null || gap === undefined || Number.isNaN(gap) ? 'none' : gapDirection(gap, thresholds);
    if (state === 'none' || state === null) {
        return { state: 'none', text: '—', sr: label || 'No AI comparison' };
    }
    const text = formatPctSigned(gap);
    return { state, text, sr: label || `${text} vs AI, ${AI_PRICE_STATES[state]}` };
};

/**
 * Asking vs AI price chip, on the shared scale. Give it a `unit` (same state,
 * text and full-sentence label as the inventory table's AI price cell) or an
 * aggregate `gap` fraction (e.g. a branch or the whole lot).
 * The visible chip is aria-hidden; a visually hidden sentence carries the
 * meaning, so the state never relies on colour alone.
 */
export function GapChip({ unit, gap, label }) {
    const { gapThresholds } = useDealershipSettings();
    const view = unit
        ? { state: aiPriceState(unit, gapThresholds), text: aiPriceChipText(unit, gapThresholds), sr: aiPriceLabel(unit, gapThresholds) }
        : aggregateView(gap, label, gapThresholds);
    return (
        <span className="v2-gapchip" data-state={view.state} title={view.sr}>
            <span className={`v2-chip v2-chip--${GAP_CHIP_TONES[view.state]}`} aria-hidden="true">{view.text}</span>
            <span className="v2-sr">{view.sr}</span>
        </span>
    );
}

GapChip.propTypes = {
    unit: PropTypes.object,
    gap: PropTypes.number,
    label: PropTypes.string,
};
